import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  Booking,
  EmailInterface,
  newGuideInterface,
  UserInterface,
} from '../../core/models';
import { format } from 'date-fns';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { emailPreviewSignature } from '../../core/emailPreviewSignature';
import { environment } from '../../../environments/environment';
import { emailSignature } from '../../core/emailSignature';
import { AuthService } from '../../services/auth/auth.service';
import { EMAILHTMLBODY, EMAILTEXTBODY } from '../../core/emailText';

@Component({
  selector: 'app-email-preview',
  imports: [CommonModule, ReactiveFormsModule],
  standalone: true,
  template: `<div
    class="relative z-10"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      @opacity
      *ngIf="isModal"
      class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
      aria-hidden="true"
    ></div>

    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
      <div
        class="flex min-h-full items-center justify-center p-4 text-center sm:p-0"
      >
        <div
          @opacityTranslateY
          *ngIf="isModal"
          class="relative transform w-full overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-4/5 sm:p-6"
        >
          <div class="absolute right-0 top-0" (click)="onSendValue(false)">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-8 hover:stroke-columbus-blue-950 hover:cursor-pointer"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </div>
          <div>
            <div
              class="w-full mx-auto my-8 rounded-lg shadow-lg overflow-hidden bg-white"
              *ngIf="emailData"
            >
              <!-- Email Header -->
              <div class="bg-gray-100 border-b border-gray-200 p-4">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center">
                    <div class="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <div class="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                    <div class="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div class="text-base italictext-gray-500">Email Preview</div>
                </div>
              </div>

              <!-- Email Metadata -->
              <div class="border-b border-gray-200 p-4">
                <div class="flex flex-col">
                  <div
                    class="flex flex-row justify-start"
                    [ngStyle]="{
                      opacity: isDev && currentUser ? '0.5' : '1',
                      'text-decoration':
                        isDev && currentUser ? 'line-through' : 'none',
                    }"
                  >
                    <div class=" text-gray-500 font-medium pr-2">To:</div>

                    <div class="text-gray-800 italic flex flex-row flex-wrap">
                      <ng-container
                        *ngFor="let toEmail of emailData.to; let last = last"
                      >
                        <span>{{ toEmail }}</span>
                        <span *ngIf="!last" class="pr-1">, </span>
                      </ng-container>
                    </div>
                  </div>

                  <div
                    class=" text-red-500 font-medium pr-2"
                    *ngIf="isDev && currentUser"
                  >
                    DEVELOPMENT ENVIRONMENT
                  </div>

                  <div
                    class="flex flex-row justify-start"
                    *ngIf="isDev && currentUser"
                  >
                    <div class=" text-red-500 font-medium pr-2">To:</div>

                    <div class="text-red-800 italic flex flex-row flex-wrap">
                      {{ currentUser.email }}
                    </div>
                  </div>

                  <div class="flex flex-row justify-start">
                    <div class="text-gray-500 font-medium pr-2">Subject:</div>
                    <div class=" text-gray-800 font-semibold">
                      {{ emailData.subject }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email Body -->
              <div class="p-4 bg-white sm:p-6">
                <div
                  class="mb-6 overflow-x-auto"
                  [innerHTML]="sanitizedHtmlBody"
                ></div>

                <!-- Plain Text Body (shown as a fallback)
                <div
                  class="p-3 bg-gray-50 rounded-md text-gray-700 text-sm font-mono mb-6"
                >
                  {{ emailData.textBody }}
                </div>
-->

                <!-- Attachments -->
                <div class="mt-6 border-t border-gray-200 pt-4">
                  <h3 class="text-sm font-medium text-gray-500 mb-3">
                    Attachments
                  </h3>
                  <div class="flex flex-wrap flex-row justify-between gap-3">
                    <div class="flex flex-row gap-3">
                      <a
                        *ngIf="emailData.pdfUrl"
                        class="flex items-center px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                        [href]="emailData.pdfUrl"
                        target="_blank"
                      >
                        <svg
                          class="w-5 h-5 mr-2"
                          version="1.1"
                          id="_x32_"
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          viewBox="0 0 512 512"
                          xml:space="preserve"
                          fill="#000000"
                        >
                          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                          <g
                            id="SVGRepo_tracerCarrier"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          ></g>
                          <g id="SVGRepo_iconCarrier">
                            <style type="text/css">
                              .st0 {
                                fill: #000000;
                              }
                            </style>
                            <g>
                              <!-- Original surrounding shape remains intact -->
                              <path
                                class="st0"
                                d="M378.413,0H208.297h-13.182L185.8,9.314L57.02,138.102l-9.314,9.314v13.176v265.514 c0,47.36,38.528,85.895,85.896,85.895h244.811c47.353,0,85.881-38.535,85.881-85.895V85.896C464.294,38.528,425.766,0,378.413,0z M432.497,426.105c0,29.877-24.214,54.091-54.084,54.091H133.602c-29.884,0-54.098-24.214-54.098-54.091V160.591h83.716 c24.885,0,45.077-20.178,45.077-45.07V31.804h170.116c29.87,0,54.084,24.214,54.084,54.092V426.105z"
                              ></path>

                              <!-- Bigger letter "B" -->
                              <text
                                x="50%"
                                y="50%"
                                font-size="200"
                                text-anchor="middle"
                                dy=".3em"
                                class="st0"
                              >
                                B
                              </text>
                            </g>
                          </g>
                        </svg>

                        <span class="text-sm">Booking PDF</span>
                      </a>

                      <a
                        *ngIf="emailData.voucherUrl"
                        class="flex items-center px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                        [href]="emailData.voucherUrl"
                        target="_blank"
                      >
                        <svg
                          class="w-5 h-5 mr-2"
                          version="1.1"
                          id="_x32_"
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          viewBox="0 0 512 512"
                          xml:space="preserve"
                          fill="#000000"
                        >
                          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                          <g
                            id="SVGRepo_tracerCarrier"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          ></g>
                          <g id="SVGRepo_iconCarrier">
                            <style type="text/css">
                              .st0 {
                                fill: #000000;
                              }
                            </style>
                            <g>
                              <!-- Original surrounding shape remains intact -->
                              <path
                                class="st0"
                                d="M378.413,0H208.297h-13.182L185.8,9.314L57.02,138.102l-9.314,9.314v13.176v265.514 c0,47.36,38.528,85.895,85.896,85.895h244.811c47.353,0,85.881-38.535,85.881-85.895V85.896C464.294,38.528,425.766,0,378.413,0z M432.497,426.105c0,29.877-24.214,54.091-54.084,54.091H133.602c-29.884,0-54.098-24.214-54.098-54.091V160.591h83.716 c24.885,0,45.077-20.178,45.077-45.07V31.804h170.116c29.87,0,54.084,24.214,54.084,54.092V426.105z"
                              ></path>

                              <!-- Bigger letter "B" -->
                              <text
                                x="50%"
                                y="50%"
                                font-size="200"
                                text-anchor="middle"
                                dy=".3em"
                                class="st0"
                              >
                                V
                              </text>
                            </g>
                          </g>
                        </svg>

                        <span class="text-sm">Voucher</span>
                      </a>
                    </div>
                    <div class="flex flex-row">
                      <button
                        (click)="sendEmailData()"
                        type="button"
                        [disabled]="sending"
                        [attr.aria-busy]="sending"
                        class="flex flex-row items-center gap-1 rounded-md  bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
                      >
                        <svg
                          *ngIf="!sending"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="1.5"
                          stroke="currentColor"
                          class="size-4 -rotate-90"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                          />
                        </svg>

                        <svg
                          *ngIf="sending"
                          class="size-4 animate-spin text-white/30 fill-white"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>

                        {{ sending ? 'Sendet…' : 'Senden' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email Footer -->
              <div
                class="bg-gray-50 p-4 text-xs text-gray-500 text-center border-t border-gray-200"
              >
                This is a preview of the email that will be sent. It cannot be
                edited here.
              </div>
            </div>
            <div class="mt-3 text-center sm:mt-5"></div>
            <div class="mt-5 flex flex-row justify-between gap-x-6"></div>
          </div>
        </div>
      </div>
    </div>
  </div>`,
  styles: ``,
  animations: [
    trigger('opacity', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('200ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('opacityTranslateY', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(1rem)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0)' }),
        animate(
          '200ms ease-in',
          style({ opacity: 0, transform: 'translateY(1rem)' }),
        ),
      ]),
    ]),
  ],
  // Everything the template reads is computed once in ngOnInit and never changes,
  // so global ticks (touch events, the open Firestore listener) have nothing to do here.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailPreviewComponent implements OnInit {
  @Input({ required: true }) emailBooking!: Booking;
  // Owned by the parent, which flips it synchronously while handling sendEmail.
  // `[disabled]` on a native button is what actually blocks the second click.
  @Input() sending = false;
  @Output() close = new EventEmitter<boolean>();
  @Output() sendEmail = new EventEmitter<EmailInterface>();
  isModal = true;
  fb = inject(FormBuilder);
  _sanitizer = inject(DomSanitizer);
  auth = inject(AuthService);
  emailData: EmailInterface | undefined = undefined;
  currentUser: UserInterface | undefined | null = undefined;
  isDev = !environment.production;

  // Must stay a field, never a getter: bypassSecurityTrustHtml() returns a new
  // object identity per call, so a getter makes Angular rewrite innerHTML on
  // every change detection cycle. On iOS that DOM mutation lands inside the
  // synthetic mousemove->click sequence, WebKit's ContentChangeObserver sees it
  // and cancels the click — every button in this modal stops working.
  sanitizedHtmlBody: SafeHtml | null = null;
  ngOnInit(): void {
    if (this.emailBooking) {
      const emailRecipient: string[] = [];

      Object.entries(this.emailBooking.guides ?? {}).forEach(([key, value]) => {
        if (value.guideDetails.email && !value.storno.isActive) {
          emailRecipient.push(value.guideDetails.email);
        }
      });

      const date = new Date(
        this.emailBooking.duration.startTime.seconds * 1000,
      );
      this.currentUser = this.auth.currentUserSig();
      this.emailData = {
        to: emailRecipient,
        from: this.currentUser?.email as string,
        subject: `${format(date, 'dd.MM.yyyy')} / ${this.createTours(this.emailBooking.guides)} / ${this.emailBooking.ship.shipName} / ${this.emailBooking.duration.tourCode}`,
        textBody: EMAILTEXTBODY,
        htmlBody: EMAILHTMLBODY,
        pdfUrl: this.emailBooking.pdfUrl as string,
        voucherUrl: this.emailBooking.voucher?.voucherUrl as string,
        formatedDate: format(
          new Date(this.emailBooking.duration.date.seconds * 1000),
          'yyMMdd',
        ),
        tourCode: this.emailBooking.duration.tourCode as string,
        invoiceNr: this.emailBooking.cruiseDetails?.invoiceNumber as string,
      };

      if (this.currentUser) {
        this.sanitizedHtmlBody = this._sanitizer.bypassSecurityTrustHtml(
          this.emailData.htmlBody + emailPreviewSignature(this.currentUser),
        );
      }
    }
  }

  onSendValue(value: boolean) {
    this.close.emit(value);
  }

  sendEmailData() {
    // Without a signed-in user the payload carries no `from` and the backend rejects it.
    if (!this.emailData || !this.currentUser) {
      return;
    }

    // Emit a copy. emailData stays pristine, so retrying after a failed send does not
    // append the signature a second time.
    this.sendEmail.emit({
      ...this.emailData,
      to: this.isDev ? [this.currentUser.email] : this.emailData.to,
      htmlBody: this.emailData.htmlBody + emailSignature(this.currentUser),
    });
  }

  createTours(guides: Record<string, newGuideInterface> | undefined) {
    if (!guides) return 'TBA';
    let tourList: Record<string, number> = {};
    let tours = '';

    Object.entries(guides).forEach(([id, guide]) => {
      const tourName = guide.tour.tourInfo.tourName || 'TBA';
      tourList[tourName] = (tourList[tourName] || 0) + 1;
    });

    Object.entries(tourList).forEach(([tour, number]) => {
      tours += tour + ' / ';
    });

    tours = tours.slice(0, tours.length - 3);

    return tours;
  }
}
